import {
  useBusinessTypes,
  useCustomerTypes,
  useDistributionChannel,
} from '@diamond/sol-admin/features/shipto';
import { useEffect, useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';

import type { CreateVoucherRequest } from '../types';

const formatList = (
  values: readonly string[] | undefined,
  options: readonly { value: string; label: string }[],
  prefix: string
) => {
  if (!values?.length) {
    return null;
  }

  const labels = values
    .map(
      (value) =>
        options.find((option) => option.value === value)?.label ?? value
    )
    .join(', ');

  return `${prefix}: ${labels}`;
};

export const useVoucherPreview = ({
  form,
}: {
  form: UseFormReturn<CreateVoucherRequest>;
}) => {
  const { watch } = form;
  const formData = watch();

  // Fetch options from API for label mapping
  const { options: customerTypeOptions } = useCustomerTypes();
  const { options: branchOptions } = useDistributionChannel();
  const { options: restaurantCategoryOptions } = useBusinessTypes();

  useEffect(() => {
    return () => {
      const thumbnailUrl = formData.banner_url;
      if (thumbnailUrl && thumbnailUrl.startsWith('blob:')) {
        URL.revokeObjectURL(thumbnailUrl);
      }
    };
  }, [formData.banner_url]);

  const targetConfig = formData.target_configuration;
  const conditionConfig = formData.condition_configuration;
  const acquisitionConfig = formData.acquisition_configuration;

  const requirementTypeLabel =
    conditionConfig?.condition_type === 'MIN_PURCHASE'
      ? 'Minimal pembelian'
      : 'Reguler';

  const mechanismLabel =
    acquisitionConfig?.acquisition_type === 'ACCOUNT_ACTIVATION'
      ? 'Aktivasi akun'
      : 'Reguler';

  const accountAgeLabel = acquisitionConfig?.valid_day_since_account_signup
    ? `${acquisitionConfig.valid_day_since_account_signup} hari`
    : undefined;

  const bulletItems = useMemo(() => {
    const items: string[] = [];

    if (targetConfig?.target_type === 'SHIP_TO') {
      items.push('Berlaku untuk daftar ship to yang diupload.');
    } else {
      const customerTypeLabel = formatList(
        targetConfig?.business_segmentations,
        customerTypeOptions,
        'Tipe customer'
      );
      if (customerTypeLabel) {
        items.push(customerTypeLabel);
      }

      const branchLabel = formatList(
        targetConfig?.distribution_channels,
        branchOptions,
        'Cabang'
      );
      if (branchLabel) {
        items.push(branchLabel);
      }

      const restaurantCategoryLabel = formatList(
        targetConfig?.business_categories,
        restaurantCategoryOptions,
        'Kategori restoran'
      );
      if (restaurantCategoryLabel) {
        items.push(restaurantCategoryLabel);
      }
    }

    items.push(`Jenis syarat: ${requirementTypeLabel}`);

    if (targetConfig?.max_use_per_user) {
      items.push(
        `Maksimal pemakaian voucher ${targetConfig.max_use_per_user}x`
      );
    }

    if (!conditionConfig?.is_allow_combine_other_voucher) {
      items.push('Tidak dapat digabung dengan voucher lain');
    }

    if (!conditionConfig?.is_allow_combine_other_promo) {
      items.push('Tidak dapat digabung dengan promo lain');
    }

    if (!conditionConfig?.is_allow_combine_other_special_price) {
      items.push('Tidak dapat digunakan bersama Price List Khusus (PLK)');
    }

    items.push(`Cara mendapatkan: ${mechanismLabel}`);

    if (accountAgeLabel) {
      items.push(`Berlaku sejak akun dibuat selama ${accountAgeLabel}`);
    }

    return items;
  }, [
    targetConfig?.target_type,
    targetConfig?.business_segmentations,
    targetConfig?.distribution_channels,
    targetConfig?.business_categories,
    targetConfig?.max_use_per_user,
    conditionConfig?.condition_type,
    conditionConfig?.is_allow_combine_other_voucher,
    conditionConfig?.is_allow_combine_other_promo,
    conditionConfig?.is_allow_combine_other_special_price,
    acquisitionConfig?.acquisition_type,
    acquisitionConfig?.valid_day_since_account_signup,
    requirementTypeLabel,
    mechanismLabel,
    accountAgeLabel,
    customerTypeOptions,
    branchOptions,
    restaurantCategoryOptions,
  ]);

  return {
    formData,
    bulletItems,
    mechanismLabel,
  };
};
